import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { Pair } from '@kodiak-finance/v2-sdk'
import { getV2PositionsRefetchPolicy } from 'config/RefetchPolicies'
import { useGetAllV2PositionsByOwner, useV2PositionByPair as useV2PositionByPairQuery } from 'hooks/subgraph/v2'
import { useTokens } from 'hooks/Tokens'
import { useConnection } from 'hooks/useConnection'
import useDebounce from 'hooks/useDebounce'
import JSBI from 'jsbi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useHasRecentPositionTransactions } from 'state/transactions/useRecentPositionTransactions'
import { parseEther, parseUnits } from 'viem'

export interface V2PositionDetails {
  pair: Pair
  liquidityTokenBalance: CurrencyAmount<Token>
  totalSupply: CurrencyAmount<Token>
  valueUSD: number
}

interface UseUserV2PositionsResults {
  loading: boolean
  positions: V2PositionDetails[]
  totalCount: number
  currentPage: number
  totalPages: number
  goToPage: (page: number) => void
  refetch: () => void
  isSearching: boolean
}

export interface UseV2PositionByPairResults {
  loading: boolean
  position: V2PositionDetails | undefined
  refetch: () => void
}

interface PaginationParams {
  pageSize?: number
  enablePagination?: boolean
}

export const V2_POSITIONS_PER_PAGE = 50

export function useUserV2Positions(
  userAddress?: string | null,
  paginationParams: PaginationParams = {},
  searchTerm?: string | null
): UseUserV2PositionsResults {
  const { pageSize = V2_POSITIONS_PER_PAGE, enablePagination = true } = paginationParams
  const { chainId } = useConnection()
  const normalizedSearchTerm = typeof searchTerm === 'string' ? searchTerm : ''

  const hasRecentTransactions = useHasRecentPositionTransactions()

  const [currentPage, setCurrentPage] = useState(0)

  const debouncedSearchTerm = useDebounce(normalizedSearchTerm, 300)

  const isSearching = useMemo(() => {
    const hasActiveSearch = normalizedSearchTerm && normalizedSearchTerm.trim()
    const hasRawSearchButNotDebounced = hasActiveSearch && normalizedSearchTerm !== debouncedSearchTerm
    return Boolean(hasRawSearchButNotDebounced)
  }, [normalizedSearchTerm, debouncedSearchTerm])

  const {
    data: rawPositionData,
    isLoading,
    error,
    refetch: refetchPositions,
  } = useGetAllV2PositionsByOwner(
    {
      user: userAddress || '',
      includeZeroBalance: false,
    },
    getV2PositionsRefetchPolicy(hasRecentTransactions)
  )

  const tokenAddresses = useMemo(() => {
    if (!rawPositionData?.liquidityPositionV2S) return []

    const addressSet = new Set<string>()
    rawPositionData?.liquidityPositionV2S?.forEach((position) => {
      if (position?.pair?.token0?.id) addressSet.add(position.pair.token0.id.toLowerCase())
      if (position?.pair?.token1?.id) addressSet.add(position.pair.token1.id.toLowerCase())
    })

    return Array.from(addressSet)
  }, [rawPositionData?.liquidityPositionV2S])

  const tokensFromHook = useTokens(tokenAddresses)

  const tokens = useMemo(() => {
    return Array.isArray(tokensFromHook) ? tokensFromHook.filter((token): token is Token => !!token) : []
  }, [tokensFromHook])

  const tokenMap = useMemo(() => {
    return tokens.reduce((map, token) => {
      return {
        ...map,
        [token.address.toLowerCase()]: token,
      }
    }, {} as Record<string, Token>)
  }, [tokens])

  const allProcessedPositions = useMemo(() => {
    if (!rawPositionData?.liquidityPositionV2S || !chainId || tokens.length === 0) return []

    try {
      return rawPositionData.liquidityPositionV2S
        .filter((position) => parseFloat(position.liquidityTokenBalance) > 0)
        .map((position) => {
          const pairData = position.pair
          const token0Address = pairData.token0.id.toLowerCase()
          const token1Address = pairData.token1.id.toLowerCase()

          const token0 =
            tokenMap[token0Address] ||
            new Token(
              chainId,
              pairData.token0.id,
              parseInt(pairData.token0.decimals),
              pairData.token0.symbol,
              pairData.token0.name
            )

          const token1 =
            tokenMap[token1Address] ||
            new Token(
              chainId,
              pairData.token1.id,
              parseInt(pairData.token1.decimals),
              pairData.token1.symbol,
              pairData.token1.name
            )

          const reserve0 = CurrencyAmount.fromRawAmount(
            token0,
            JSBI.BigInt(parseUnits(pairData.reserve0, token0.decimals).toString())
          )

          const reserve1 = CurrencyAmount.fromRawAmount(
            token1,
            JSBI.BigInt(parseUnits(pairData.reserve1, token1.decimals).toString())
          )

          const pair = new Pair(reserve0, reserve1)

          const liquidityTokenBalance = CurrencyAmount.fromRawAmount(
            pair.liquidityToken,
            JSBI.BigInt(parseEther(position.liquidityTokenBalance).toString())
          )

          const poolValueUSD = parseFloat(pairData.reserveUSD)
          const poolTotalSupply = parseFloat(pairData.totalSupply)
          const userShare = parseFloat(position.liquidityTokenBalance) / poolTotalSupply
          const valueUSD = poolValueUSD * userShare

          const totalSupply = CurrencyAmount.fromRawAmount(
            pair.liquidityToken,
            JSBI.BigInt(parseEther(pairData.totalSupply).toString())
          )

          return {
            pair,
            liquidityTokenBalance,
            totalSupply,
            valueUSD,
          }
        })
        .filter((position) => position.valueUSD > 0)
        .sort((a, b) => b.valueUSD - a.valueUSD)
    } catch (e) {
      console.error('Error processing V2 positions from subgraph:', e)
      return []
    }
  }, [rawPositionData?.liquidityPositionV2S, chainId, tokens, tokenMap])

  const searchFilteredPositions = useMemo(() => {
    if (!allProcessedPositions.length) return []

    const searchQuery = debouncedSearchTerm?.trim().toLowerCase()
    if (!searchQuery) return allProcessedPositions

    // Check if search query is an address
    const isAddressLike = /^0x[a-fA-F0-9]{40}$/i.test(searchQuery)

    return allProcessedPositions.filter((position) => {
      const { pair } = position
      const token0 = pair.token0
      const token1 = pair.token1

      if (isAddressLike) {
        // Search by token addresses
        return token0.address.toLowerCase() === searchQuery || token1.address.toLowerCase() === searchQuery
      } else {
        // Search by token symbols and names
        return (
          token0.symbol?.toLowerCase().includes(searchQuery) ||
          token0.name?.toLowerCase().includes(searchQuery) ||
          token1.symbol?.toLowerCase().includes(searchQuery) ||
          token1.name?.toLowerCase().includes(searchQuery)
        )
      }
    })
  }, [allProcessedPositions, debouncedSearchTerm])

  const { positions, totalCount, totalPages } = useMemo(() => {
    const filteredPositions = searchFilteredPositions

    if (!enablePagination) {
      return {
        positions: filteredPositions,
        totalCount: filteredPositions.length,
        totalPages: 1,
      }
    }

    const startIndex = currentPage * pageSize
    const endIndex = startIndex + pageSize
    const paginatedPositions = filteredPositions.slice(startIndex, endIndex)

    return {
      positions: paginatedPositions,
      totalCount: filteredPositions.length,
      totalPages: Math.ceil(filteredPositions.length / pageSize),
    }
  }, [enablePagination, currentPage, pageSize, searchFilteredPositions])

  const goToPage = useCallback((page: number) => {
    setCurrentPage(page)
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    setCurrentPage(0)
  }, [debouncedSearchTerm, userAddress])

  const refetch = useCallback(() => {
    setCurrentPage(0)
    refetchPositions()
  }, [refetchPositions])

  if (error) {
    console.error('Error fetching V2 positions from subgraph:', error)
  }

  return {
    loading: isLoading,
    positions,
    totalCount,
    currentPage,
    totalPages,
    goToPage,
    refetch,
    isSearching,
  }
}

export function useV2PositionByPair(
  userAddress?: string | null,
  pairAddress?: string | null,
  chainId?: number | null
): UseV2PositionByPairResults {
  const hasRecentTransactions = useHasRecentPositionTransactions()

  const {
    data: rawPositionData,
    isLoading,
    error,
    refetch: refetchPosition,
  } = useV2PositionByPairQuery(
    {
      user: userAddress || '',
      pairAddress: pairAddress || '',
      includeZeroBalance: false,
    },
    getV2PositionsRefetchPolicy(hasRecentTransactions)
  )

  const tokenAddresses = useMemo(() => {
    const position = rawPositionData?.liquidityPositionV2S?.[0]
    if (!position?.pair) return []

    const addressSet = new Set<string>()
    if (position.pair.token0?.id) addressSet.add(position.pair.token0.id.toLowerCase())
    if (position.pair.token1?.id) addressSet.add(position.pair.token1.id.toLowerCase())

    return Array.from(addressSet)
  }, [rawPositionData?.liquidityPositionV2S])

  const tokensFromHook = useTokens(tokenAddresses)

  const tokens = useMemo(() => {
    return Array.isArray(tokensFromHook) ? tokensFromHook.filter((token): token is Token => !!token) : []
  }, [tokensFromHook])

  const tokenMap = useMemo(() => {
    return tokens.reduce((map, token) => {
      return {
        ...map,
        [token.address.toLowerCase()]: token,
      }
    }, {} as Record<string, Token>)
  }, [tokens])

  const processedPosition = useMemo((): V2PositionDetails | undefined => {
    const position = rawPositionData?.liquidityPositionV2S?.[0]
    if (!position || !chainId || tokens.length === 0) return undefined

    try {
      const pairData = position.pair
      const token0Address = pairData.token0.id.toLowerCase()
      const token1Address = pairData.token1.id.toLowerCase()

      const token0 =
        tokenMap[token0Address] ||
        new Token(
          chainId,
          pairData.token0.id,
          parseInt(pairData.token0.decimals),
          pairData.token0.symbol,
          pairData.token0.name
        )

      const token1 =
        tokenMap[token1Address] ||
        new Token(
          chainId,
          pairData.token1.id,
          parseInt(pairData.token1.decimals),
          pairData.token1.symbol,
          pairData.token1.name
        )

      const reserve0 = CurrencyAmount.fromRawAmount(
        token0,
        JSBI.BigInt(parseUnits(pairData.reserve0, token0.decimals).toString())
      )

      const reserve1 = CurrencyAmount.fromRawAmount(
        token1,
        JSBI.BigInt(parseUnits(pairData.reserve1, token1.decimals).toString())
      )

      const pair = new Pair(reserve0, reserve1)

      const liquidityTokenBalance = CurrencyAmount.fromRawAmount(
        pair.liquidityToken,
        JSBI.BigInt(parseEther(position.liquidityTokenBalance).toString())
      )

      const totalSupply = CurrencyAmount.fromRawAmount(
        pair.liquidityToken,
        JSBI.BigInt(parseEther(pairData.totalSupply).toString())
      )

      const poolValueUSD = parseFloat(pairData.reserveUSD)
      const poolTotalSupply = parseFloat(pairData.totalSupply)
      const userShare = parseFloat(position.liquidityTokenBalance) / poolTotalSupply
      const valueUSD = poolValueUSD * userShare

      return {
        pair,
        liquidityTokenBalance,
        totalSupply,
        valueUSD,
      }
    } catch (e) {
      console.error('Error processing V2 position from subgraph:', e)
      return undefined
    }
  }, [rawPositionData, chainId, tokens, tokenMap])

  const refetch = useCallback(() => {
    refetchPosition()
  }, [refetchPosition])

  if (error) {
    console.error('Error fetching V2 position from subgraph:', error)
  }

  return {
    loading: isLoading,
    position: processedPosition,
    refetch,
  }
}
